import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useCollection } from "../useData";
import { resolveImg } from "../api";
import SEO from "../components/SEO";
import NotFound from "../components/NotFound";

const CommercialDetail = () => {
  const { id } = useParams();
  const commercial = useCollection("commercial") || [];
  const item = commercial.find((c) => c.id === id);
  const [idx, setIdx] = useState(0);

  useEffect(() => { setIdx(0); }, [id]);

  if (!commercial.length) return null;
  if (!item) return <NotFound section="This work" backTo="/commercial" backLabel="Back to commercial" />;

  const images = item.images && item.images.length ? item.images : (item.cover ? [item.cover] : []);
  const currentIdx = commercial.findIndex((c) => c.id === item.id);
  const nextItem = commercial[(currentIdx + 1) % commercial.length];

  const next = () => images.length && setIdx((i) => (i + 1) % images.length);
  const prev = () => images.length && setIdx((i) => (i - 1 + images.length) % images.length);

  return (
    <div className="px-6 md:px-10 max-w-[1100px] mx-auto">
      <SEO
        title={`${item.client} — ${item.title}`}
        description={item.description || `${item.title} for ${item.client} — ${item.category || "commercial"} by Alice Lisi.`}
        image={item.cover || images[0]}
        path={`/commercial/${item.id}`}
      />
      <div className="pt-2 pb-6 flex items-baseline justify-end">
        {item.externalUrl ? (
          <a href={item.externalUrl} target="_blank" rel="noreferrer" className="font-serif italic tlink tlink-mute text-sm">
            View on alicelisi.com →
          </a>
        ) : null}
      </div>

      {/* Gallery — natural aspect on paper background */}
      <div className="max-w-[900px] mx-auto">
        <div className="relative w-full flex items-center justify-center bg-[color:var(--paper)] fade-in" style={{ height: "75vh" }}>
          {images.map((src, i) => (
            <img
              key={i}
              src={resolveImg(src)}
              alt={`${item.title} ${i + 1}`}
              className={`absolute max-h-full max-w-full object-contain transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
            />
          ))}
          <button onClick={prev} className="absolute left-0 top-0 h-full w-1/2 z-10" aria-label="Previous" style={{ cursor: "w-resize" }} />
          <button onClick={next} className="absolute right-0 top-0 h-full w-1/2 z-10" aria-label="Next" style={{ cursor: "e-resize" }} />
        </div>

        <div className="mt-4 flex items-center justify-center">
          <div className="flex gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? "bg-[color:var(--ink)]" : "bg-[color:var(--line)]"}`}
                aria-label={`Image ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Description — no label */}
      <div className="mt-20 max-w-[900px] mx-auto">
        <h1 className="font-serif italic text-3xl md:text-4xl leading-[1.15] mb-4">{item.title}</h1>
        <p className="font-serif text-xl md:text-2xl leading-[1.35]">{item.description}</p>
        {item.credits && <p className="font-serif text-[color:var(--ink-mute)] mt-5">{item.credits}</p>}
      </div>

      {/* Credits — no label */}
      <div className="mt-16 max-w-[900px] mx-auto pb-4">
        <dl className="grid grid-cols-12 gap-y-2 font-serif">
          <dt className="col-span-4 text-[color:var(--ink-mute)]">Year</dt><dd className="col-span-8">{item.year}</dd>
          <dt className="col-span-4 text-[color:var(--ink-mute)]">Type</dt><dd className="col-span-8">{item.category}</dd>
          <dt className="col-span-4 text-[color:var(--ink-mute)]">Client</dt><dd className="col-span-8">{item.client}</dd>
          <dt className="col-span-4 text-[color:var(--ink-mute)]">Photographer</dt><dd className="col-span-8">Alice Lisi</dd>
          <dt className="col-span-4 text-[color:var(--ink-mute)]">Frames</dt><dd className="col-span-8 counter">{images.length}</dd>
        </dl>
      </div>

      {/* Next */}
      <div className="mt-32 pt-8 border-t border-[color:var(--line)] flex items-baseline justify-between">
        <span className="font-serif italic text-[color:var(--ink-mute)]">Next</span>
        <Link to={`/commercial/${nextItem.id}`} className="font-serif italic text-2xl tlink">{nextItem.client} →</Link>
      </div>
    </div>
  );
};

export default CommercialDetail;
